import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Button } from "@/components/ui/button";
import { Save, ToggleLeft, SlidersHorizontal } from "lucide-react";
import { toast } from "sonner";

export type RatingMode = 'checkbox' | 'slider';

interface RatingModeSettingsProps {
  ratingMode: RatingMode;
  onSave: (mode: RatingMode) => void;
}

const RatingModeSettings = ({ ratingMode, onSave }: RatingModeSettingsProps) => {
  const handleSave = (mode: RatingMode) => {
    onSave(mode);
    toast.success("Bewertungsmodus gespeichert");
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Bewertungsmodus</CardTitle>
        <CardDescription>
          Wählen Sie, wie Bewertungen in der Matrix eingegeben werden sollen
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <RadioGroup
          value={ratingMode}
          onValueChange={(value) => handleSave(value as RatingMode)}
          className="space-y-4"
        >
          <div className="flex items-start space-x-4 p-4 border rounded-lg hover:bg-accent/50 transition-colors cursor-pointer">
            <RadioGroupItem value="checkbox" id="checkbox" className="mt-1" />
            <Label htmlFor="checkbox" className="flex-1 cursor-pointer">
              <div className="flex items-center gap-2 mb-2">
                <ToggleLeft className="h-5 w-5 text-primary" />
                <span className="font-semibold">4-Stufen-System (Checkboxen)</span>
              </div>
              <p className="text-sm text-muted-foreground">
                Klassische Bewertung mit vier Optionen: Keine Erfüllung, Teilweise erfüllt, 
                Überwiegend erfüllt, Voll erfüllt. Jede Option wird per Checkbox ausgewählt.
              </p>
              <div className="mt-3 flex gap-2 flex-wrap">
                <span className="px-2 py-1 text-xs rounded bg-destructive/20 text-destructive">Keine Erfüllung</span>
                <span className="px-2 py-1 text-xs rounded bg-orange-500/20 text-orange-600">Teilweise</span>
                <span className="px-2 py-1 text-xs rounded bg-yellow-500/20 text-yellow-600">Überwiegend</span>
                <span className="px-2 py-1 text-xs rounded bg-green-500/20 text-green-600">Voll erfüllt</span>
              </div>
            </Label>
          </div>

          <div className="flex items-start space-x-4 p-4 border rounded-lg hover:bg-accent/50 transition-colors cursor-pointer">
            <RadioGroupItem value="slider" id="slider" className="mt-1" />
            <Label htmlFor="slider" className="flex-1 cursor-pointer">
              <div className="flex items-center gap-2 mb-2">
                <SlidersHorizontal className="h-5 w-5 text-primary" />
                <span className="font-semibold">Schieberegler-System</span>
              </div>
              <p className="text-sm text-muted-foreground">
                Standardmäßig ist alles grün (volle Punkte). Bei jedem Vorfall wird der Regler 
                um einen Punkt reduziert. Die Farbe ändert sich je nach Punktzahl von Grün über 
                Orange zu Rot.
              </p>
              <div className="mt-3 flex items-center gap-1">
                <span className="w-6 h-6 rounded bg-green-500 flex items-center justify-center text-xs text-white font-bold">10</span>
                <span className="w-6 h-6 rounded bg-green-400 flex items-center justify-center text-xs text-white font-bold">9</span>
                <span className="w-6 h-6 rounded bg-lime-400 flex items-center justify-center text-xs text-white font-bold">8</span>
                <span className="w-6 h-6 rounded bg-yellow-400 flex items-center justify-center text-xs text-white font-bold">7</span>
                <span className="w-6 h-6 rounded bg-amber-400 flex items-center justify-center text-xs text-white font-bold">6</span>
                <span className="w-6 h-6 rounded bg-orange-400 flex items-center justify-center text-xs text-white font-bold">5</span>
                <span className="w-6 h-6 rounded bg-orange-500 flex items-center justify-center text-xs text-white font-bold">4</span>
                <span className="w-6 h-6 rounded bg-red-400 flex items-center justify-center text-xs text-white font-bold">3</span>
                <span className="w-6 h-6 rounded bg-red-500 flex items-center justify-center text-xs text-white font-bold">2</span>
                <span className="w-6 h-6 rounded bg-red-600 flex items-center justify-center text-xs text-white font-bold">1</span>
                <span className="w-6 h-6 rounded bg-red-700 flex items-center justify-center text-xs text-white font-bold">0</span>
              </div>
            </Label>
          </div>
        </RadioGroup>
      </CardContent>
    </Card>
  );
};

export default RatingModeSettings;
